import api from '../../../services/api';

/**
 * API client for server-side order-create wizard drafts (`/api/order-drafts`).
 *
 * A draft holds the in-progress form state of a create wizard as a JSON
 * `payload`. The backend scopes every request to the authenticated user, so the
 * client never sends a userId — a user only ever sees their own drafts.
 *
 * `type` distinguishes the two wizards. This is unrelated to the
 * "SupplierOrder".status = 'DRAFT' lifecycle state.
 */
export type OrderDraftType = 'CUSTOMER' | 'SUPPLIER';

export interface OrderDraftSummary {
  id: string;
  userId: string;
  type: OrderDraftType;
  name: string;
  createdAt: string;
  updatedAt: string;
}

export interface OrderDraft extends OrderDraftSummary {
  payload: Record<string, any>;
}

export interface OrderDraftInput {
  name: string;
  payload: Record<string, any>;
}

/** List the current user's drafts of one type (metadata only, no payload). */
export async function listOrderDrafts(type: OrderDraftType): Promise<OrderDraftSummary[]> {
  const response = await api.get('/order-drafts', { params: { type } });
  return response.data;
}

/** Fetch one draft with its full payload. */
export async function fetchOrderDraft(id: string): Promise<OrderDraft> {
  const response = await api.get(`/order-drafts/${id}`);
  return response.data;
}

/** Create a new named draft. */
export async function createOrderDraft(
  input: OrderDraftInput & { type: OrderDraftType }
): Promise<OrderDraft> {
  const response = await api.post('/order-drafts', input);
  return response.data;
}

/** Update a draft's name + payload. */
export async function updateOrderDraft(id: string, input: OrderDraftInput): Promise<OrderDraft> {
  const response = await api.put(`/order-drafts/${id}`, input);
  return response.data;
}

/** Delete a draft. */
export async function deleteOrderDraft(id: string): Promise<void> {
  await api.delete(`/order-drafts/${id}`);
}
